import { ApiResult, httpRequest, Urls } from './Shared';
import { Event } from './Property';
import {
    RelationshipType,
    PreferredTourFormat,
    VirtualTourStatus,
    SourceType,
    TourType
} from '../utilities/Enums';
import * as moment from 'moment';
import { momentDateFormat } from '../utilities/formatting';

const PROSPECT_URL = '/Prospects';
const MY_TOURS = '/MyTours'

export interface ContactInfo {
    firstName?: string;
    lastName?: string;
    dateCreated?: string;
    email?: string;
    partnerName?: string;
    phone?: string;
    moveInDate?: string;
    apartmentType?: number;
    apartmentTypeDescription?: string;
    contactPreference?: number;
    contactPreferenceDisplay?: string;
    pet?: boolean;
    desiredLeaseLength?: number;
    source?: string;
    sourceType: SourceType;
    preferSelfGuided?: boolean;
    preferredTourFormat?: PreferredTourFormat;
    tourType: TourType;
    tourTypeDisplay: string;
    excludeFromStatistics: boolean;
    birthDay?: number;
    birthMonth?: number;
    birthYear?: number;
    additionalResidents: AdditionalResident[];
    virtualTourStatus?: VirtualTourStatus;

}

export interface AdditionalResident {
    id?: number;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    relationshipType: RelationshipType;
}

export interface TourInfo {
    id?: number;
    event?: Event;
    date: string;
    tourTypeDisplay: string;
    status?: string;
    guid: string;
    applicationSubmitted: boolean;
}

export interface FlatProspect extends ContactInfo {
    publicKey?: string;
    tourInterval: Event;
    status?: string;
    tourId?: number;
    virtualStatus?: string;
}

export interface Prospect {
    publicKey?: string;
    contactInfo: ContactInfo;
    tourInfo: TourInfo;
    propertyName?: string;
    idCheckStatus?: string;
    creditCardStatus?: string;
    keyStatus?: string;
    partnerName?: string;
}

export interface ProspectTourInfo {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    propertyName: string;
    partner: string;
    tourCreationDate: string;
    tourStartDate: string;
    tourStartTime: string;
    tourEndTime: string;
    tourStatus: string;
}

export interface ProspectInfo {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    initialDate: string;
    lastTourDate: string;
    lastTourDatePropertyName: string;
    tourStatus: string;
    applicationStatus: string;
    totalPropertiesVisited: number;
    prospectUserId: number;
    blockedStatus: boolean;
}

export interface SimpleProspect {
    publicKey?: string;
    contactInfo: ContactInfo;
    tourInfo: TourInfo;
    propertyName?: string;
    idCheckStatus?: string;
    creditCardStatus?: string;
    keyStatus?: string;
}

export interface ConfirmedProspect extends Prospect {
    tourInfo: TourInfo;
}

export interface ProspectRequest {
    startDate: string | null;
    endDate: string | null;
    propertyId: number;
}

export interface ProspectsRequest {
    startDate: string | null;
    endDate: string | null;
    propertyIds: number[];
}

export interface FeedbackItemDto {
    location: string;
    question: string;
    answer: string;
    type: string;
}

export interface HistoryItemDto {
    localTimestamp: string;
    timestamp: string;
    location: string;
}

export interface AccessCodesDto {
    doorkingCode: string | undefined;
    remoteLockPin: string | undefined;
    algorithmicRemoteLockPin: string | undefined;
    igloohomeCodes: IgloohomeCode[] | undefined;
    masterLockCodes: MasterLockCode[] | undefined;
    brivoPin: string | undefined;
    manualAccessCode: string | undefined;
    bmxCode: string | undefined;
    bmxDirectIntegrationPinCode: string | undefined;
    bmxDirectIntegrationQRCode: string | undefined;

    latchLink?: string;
    smartRentAccessCodes: any | undefined;
    geokeyAccessCodes: any | undefined;
}

export interface bmxCodes {
    accessCode: string;
    qrCodeUrl: string;
}

export interface IgloohomeCode {
    code: string;
    lockId?: number;
    lockUniqueId: string;
    lockName: string;
}

export interface MasterLockCode {
    code: string;
    lockId?: number;
    expiryDate?: string;
    lockIdentifier: string;
    lockName: string;
}

export interface TourFeedbackDto {
    propertyName: string;
    history: HistoryItemDto[];
    feedback: FeedbackItemDto[];
    accessCodes: AccessCodesDto;
    noShow: boolean;
    bmxAccessCodes: bmxCodes[];
}

export interface TourAccess {
    prospect: Prospect;
}

export interface ApplessTourInfo {
    urlSlug: string | undefined;
    leasingUrl: string | undefined;
    unitInstructions: string | undefined;
    isAmazonKeyEnabled: boolean | undefined;
    propertyImage: string | undefined;
    propertyName: string | undefined;
    tourStart: string;
    tourEnd: string;
    accessCodes: AccessCodesDto;
    mapUrl: string | undefined;
    hasUnitMap: boolean;
    altPropertyImage: string | undefined;
    bmxAccessCodes: bmxCodes[];
}

export interface ProspectAmazonPrecheck {
    latitude: number;
    longitude: number;
    accessPointId: string;
}

export interface ProspectAmazonUnlock {
    "user": {
        "id": string
    },
    "userLocation": {
        "latitude": number,
        "longitude": number,
        "accuracy": number,
        "timestamp": number
    },
    "accessPointId": string,
    "correlationId": string,
}

export interface PhoneAndEmail {
    email: string;
    phoneNumber: string;
}

export interface UniqueProspectCountsDto {
    uniqueProspectsWithTour: number;
    uniqueProspectsWithoutTour: number;
}

export interface AllProspectDataExportTour {
    tourDay: string;
    tourStart: string;
    tourEnd: string;
    status: string;
    tourType: string;
    leaseApplied: string;
}

export interface AllProspectsDataExportDto {
    companyName: string;
    propertyName: string;
    firstName: string;
    lastName: string;
    dateCreated: string;
    email: string;
    phone: string;
    partnerName: string;
    moveInDate?: string;
    apartmentTypeDescription: string;
    contactPreference: string;
    pet: string;
    desiredLeaseLength: string;
    source: string;
    additionalResidents: number;
    ccQualified: string;
    idVerified: string;
    tour?: AllProspectDataExportTour;
}

export const forwardLeads = (
    prospectToken: string,
    propertySlug: string
): Promise<ApiResult<string>> => {
    return httpRequest<string>(
        `${Urls.BASE_URL}${PROSPECT_URL}/${propertySlug}/forwardLeads`,
        'post',
        prospectToken
    );
};

export const getRecentProspects = (): Promise<ApiResult<Prospect[]>> => {
    return httpRequest<Prospect[]>(`${Urls.BASE_URL}${PROSPECT_URL}/recent`, 'get');
};

export const getProspects = (prospectRequest: ProspectRequest): Promise<ApiResult<Prospect[]>> => {
    return httpRequest<Prospect[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}` +
        `?propertyId=${prospectRequest.propertyId}` +
        `&startDate=${prospectRequest.startDate}` +
        `&endDate=${prospectRequest.endDate}`,
        'get'
    );
};

export const getProspectsDetails = (prospectRequest: ProspectsRequest): Promise<ApiResult<ProspectInfo[]>> => {
    return httpRequest<ProspectInfo[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/prospectDetailsForProperties`,
        'post',
        {
            'propertyIds': [`${prospectRequest.propertyIds}`],
            'startDate': prospectRequest.startDate,
            'endDate': prospectRequest.endDate
        },
    );
};

export const getAmazonKeys = (guid: string) =>
    httpRequest<any>(
        `${Urls.BASE_URL}${MY_TOURS}/${guid}/GetAmazonKeys`,
        'get'
    );

export const sendCordinatesAmazon = (tourInfo: ProspectAmazonPrecheck, guid: string) =>
    httpRequest<any>(
        `${Urls.BASE_URL}${MY_TOURS}/${guid}/PreCheck`,
        'post',
        tourInfo
    );

export const unlockLocAmazon = (tourInfo: ProspectAmazonUnlock, guid: string) =>
    httpRequest<any>(
        `${Urls.BASE_URL}${MY_TOURS}/${guid}/Unlock`,
        'post',
        tourInfo
    );

export const unlockStatusLocAmazon = (tourInfo: ProspectAmazonUnlock, guid: string) =>
    httpRequest<any>(
        `${Urls.BASE_URL}${MY_TOURS}/${guid}/UnlockStatus`,
        'post',
        tourInfo
    );


export const getAllProspects = (startDate: string, endDate: string) =>
    httpRequest<Prospect[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/all?startDate=${startDate}&endDate=${endDate}`,
        'get'
    );

export const getTourFeedback = (tourId: number): Promise<ApiResult<TourFeedbackDto | null>> => {
    if (!tourId) {
        return new Promise<ApiResult<TourFeedbackDto | null>>(resolve =>
            resolve({ success: false, error: 'No tour selected', data: null })
        );
    }
    return httpRequest<TourFeedbackDto>(
        `${Urls.BASE_URL}${PROSPECT_URL}/tourfeedback/${tourId}`,
        'get'
    );
};

export const getProspectData = (info: any): Promise<ApiResult<any>> => {
    return httpRequest<any>(`${Urls.BASE_URL}${PROSPECT_URL}/prospectDetailsAndTours`, 'post', info);
};

export const getGuestCardInfo = (guid: any): Promise<ApiResult<any>> => {
    return httpRequest<any>(`${Urls.BASE_URL}${PROSPECT_URL}/guestCardInformation/${guid}`, 'get');
};

export const getTourInfo = (tourId: any): Promise<ApiResult<any>> => {
    return httpRequest<any>(`${Urls.BASE_URL}${PROSPECT_URL}/tourfeedback/${tourId}`, 'get');
};

export const getTourlessProspect = (
    propertyId: number,
    startDate: moment.Moment,
    endDate: moment.Moment
): Promise<ApiResult<Prospect[]>> =>
    httpRequest<Prospect[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/tourless?propertyId=${propertyId}` +
        `&startDate=${startDate.format(momentDateFormat)}` +
        `&endDate=${endDate.format(momentDateFormat)}`,
        'get'
    );

export const getTourlessProspectInfo = (
    propertyIds: number[],
    startDate: moment.Moment,
    endDate: moment.Moment
): Promise<ApiResult<ProspectTourInfo[]>> =>
    httpRequest<ProspectTourInfo[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/toursDetailsForProperties`,
        'post',
        {
            'startDate': startDate,
            'endDate': endDate,
            'propertyIds': propertyIds
        }
    );

export const getUniqueProspectsCount = (
    propertyId: number,
    startDate: moment.Moment,
    endDate: moment.Moment
): Promise<ApiResult<UniqueProspectCountsDto>> =>
    httpRequest<UniqueProspectCountsDto>(
        `${Urls.BASE_URL}${PROSPECT_URL}/uniqueProspects?propertyId=${propertyId}` +
        `&startDate=${startDate.format(momentDateFormat)}` +
        `&endDate=${endDate.format(momentDateFormat)}`,
        'get'
    );

export const sendTourSignupEmail = (prospectGuid: string): Promise<ApiResult<undefined>> =>
    httpRequest(
        `${Urls.BASE_URL}${PROSPECT_URL}/sendTourSignupEmail?prospectToken=${prospectGuid}`,
        'post'
    );

export const toggleProspectExclusion = (prospectToken: string): Promise<ApiResult<boolean>> => {
    return httpRequest<boolean>(`${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/exclude`, 'post');
};

export const updateTour = (prospectToken: string, tourInfo: TourInfo) =>
    httpRequest<Prospect>(
        `${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/tour`,
        'post',
        tourInfo
    );

export const deleteTour = (prospectToken: string, tourInfo: TourInfo) =>
    httpRequest<Prospect>(
        `${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/deleteTour`,
        'post',
        tourInfo
    );

export const markTourCompleted = (prospectToken: string) =>
    httpRequest<Prospect>(
        `${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/markTourCompleted`,
        'post'
    );

export const updatePhoneAndEmail = (prospectToken: string | null, phoneAndEmail: PhoneAndEmail) =>
    httpRequest<PhoneAndEmail>(
        `${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/updatePhoneAndEmail`,
        'put',
        phoneAndEmail
    );

export const blockProspect = (prospectData: any) =>
    httpRequest<any>(
        `${Urls.BASE_URL}${PROSPECT_URL}/blockProspectuser`,
        'post',
        prospectData
    );

export const getAllProspectsExportData = (prospectData: any) =>
    httpRequest<AllProspectsDataExportDto[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/allProspectsDataExport`,
        'post',
        prospectData
    );

export const getPropertyProspectDataExport = (prospectData: any) =>
    httpRequest<AllProspectsDataExportDto[]>(
        `${Urls.BASE_URL}${PROSPECT_URL}/prospectDataExport`,
        'post',
        prospectData
    );

export const markVirtualTourCompleted = (prospectToken: string) =>
    httpRequest<{}>(`${Urls.BASE_URL}${PROSPECT_URL}/${prospectToken}/completeVirtualTour`, 'post');

export const updateApplicationStatus = (
    tourGuid: string,
    applied: boolean
) =>
    httpRequest<boolean>(
        `${Urls.BASE_URL}${PROSPECT_URL}/applicationStatus/${tourGuid}/${applied}`,
        'post',
    );



// WEBPACK FOOTER //
// ./src/api/Prospect.ts